import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import PortalDropdown from "../components/PortalDropdown";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "../assets/css/developmentTask.css"
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import moment from "moment-timezone";
import PageHeader from "../components/PageHeader";
import TargetDateCell from "../components/TargetDateCell";
import useTargetDateFilter from "../hooks/useTargetDateFilter";
import RunningTaskStrip from "../components/RunningTaskStrip";
// --- Components ---
import ReportTable from "../components/ReportTable";
import Modal from "../components/Modal";
/* Shared with the Team Pending screen — see components/TaskDetailContent.jsx. */
import TaskDetailContent from "../components/TaskDetailContent";
import Loader from "../components/Loader";
import DateRangeFilter from "../components/DateRangeFilter";
import {
  displayStatus,
  formatDateOnly,
  formatDateTimeDisplay,
  lastSevenDays,
  targetDateState,
  toDateOnly,
} from "../utils/common";
import TargetDateChips from "../components/TargetDateChips";

// --- Redux ---
import {
  selectGetTaskDevelopmentList,
  selectGetTaskDevelopmentListStatus,
} from "../redux/tasks/taskSelectors";
import {
  createStatusTrackerApis,
  getTaskDevelopmentApis,
  updateDevelopmentTasksApis,
} from "../redux/tasks/taskActions";
import { getStatusClock } from "../redux/dashboard/dashboardActions";
import { useTabRefresh } from "../context/TabsContext";

// --- Team routing ---
import MoveTaskModal from "../components/MoveTaskModal";
import TeamBadge from "../components/TeamBadge";
import { taskTimelineApis } from "../redux/team/teamActions";
import {
  selectTaskTimeline,
  selectTaskTimelineStatus,
} from "../redux/team/teamSelectors";

// --------------------------------------------------------------------------
// HELPER FUNCTIONS
// --------------------------------------------------------------------------




const formatDateForPayload = (dateInput) => {
  if (!dateInput) return null;
  return moment(dateInput).format("YYYY-MM-DDTHH:mm:ss");
};

/* "The task I am on right now" — the two statuses that hold somebody's
   attention, and the same pair sp_Queries_TaskDevelopment_Update refuses a
   second start against. QA's 'Testing In Progress' belongs here because the
   server counts it: a developer testing their own fix is busy. */
const ACTIVE_STATUSES = ["In Progress", "Testing In Progress"];

/* The status vocabulary, for the column filter.
 *
 * Declared rather than inferred from the rows. The grid can work out a
 * category on its own when there is enough data to see the pattern, but this
 * screen is routinely filtered down to a handful of rows — often one — and a
 * dropdown built from one row offers exactly one status. A fixed ladder like
 * this is knowable without looking at the data, so it is stated.
 *
 * Same six StatusSelect renders, in the same order: they are a progression,
 * and sorting them alphabetically would put Complete first. */
const STATUS_FILTER_OPTIONS = [
  "Not Started",
  "In Progress",
  "On Hold",
  "Not Possible",
  "Testing",
  "Complete",
];

/* Likewise for query type — three values, none of which depend on what
   happens to be loaded. */
const QUERY_TYPE_FILTER_OPTIONS = ["New Task", "Bug", "Rework"];

// ==========================================
// 1. REUSABLE PORTAL DROPDOWN HELPER
// ==========================================
/* Menus are rendered on document.body rather than in the cell that owns them.
   A menu left inside the row is a sibling of the cells to its right, and those
   paint over it — which is how the Status menu ended up with the Move buttons
   showing through it while every other dropdown on the row was fine.

   `position` covers the common "hang below the trigger" case; pass `style`
   instead when the caller has already worked out its own placement (a menu
   that flips upward near the bottom of the window, say) and needs `bottom` or
   a measured width rather than a plain top/left. */
/* Was a local copy of PortalDropdown, and it had drifted: it took a
   fixed {top, left} computed once on click, listened to nothing, and so
   stayed hanging in the page while the row it belonged to scrolled away.
   The shared component follows its trigger when given an anchorRef and
   closes when it cannot. One copy means one behaviour on every grid. */

// ==========================================
// 2. IS UPDATE SELECT DROPDOWN
// ==========================================
const IsUpdateSelect = ({ initialValue, onChange, readOnly = false }) => {
  const [value, setValue] = useState(initialValue || "None");
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  useEffect(() => setValue(initialValue || "None"), [initialValue]);

  const getColorMap = (val) => {
    const v = String(val || "").toLowerCase();
    if (v === "backend work") return { bg: "#f3e5f5", text: "#7b1fa2" };
    if (v === "update") return { bg: "#e8f5e9", text: "#2e7d32" };
    if (v === "not update") return { bg: "#fff3e0", text: "#e65100" };
    return { bg: "#f8f9fa", text: "#6c757d" };
  };
  const colors = getColorMap(value);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly) return;
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuWidth = 150;
      let top = rect.bottom + 5;
      if (viewportHeight - rect.bottom < 150) top = rect.top - 120;

      // Keep the menu inside the viewport's right edge.
      let left = rect.left;
      if (left + menuWidth > viewportWidth - 8) left = rect.right - menuWidth;
      if (left < 8) left = 8;
      setCoords({ top, left });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: "none",
          fontSize: "0.75rem",
          fontWeight: "600",
          padding: "6px 12px",
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          cursor: readOnly ? "default" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {value}{" "}
        {!readOnly && (
          <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`}></i>
        )}
      </button>

      <PortalDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        position={coords}
      >
        <div className="p-2">
          {["Backend work", "Update", "Not update"].map((option) => (
            <div
              key={option}
              onClick={(e) => {
                e.stopPropagation();
                setValue(option);
                onChange(option);
                setIsOpen(false);

              }}
              className="d-flex align-items-center gap-2 rounded-2 px-2 py-2 mb-1 dropdown-item-hover"
              style={{
                cursor: "pointer",
                fontSize: "0.85rem",
                backgroundColor: value === option ? "#f8f9fa" : "transparent",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                value === option ? "#f8f9fa" : "transparent")
              }
            >
              <i
                className="bi bi-circle-fill"
                style={{ fontSize: "0.5rem", color: getColorMap(option).text }}
              ></i>
              <span className="text-dark">{option}</span>
            </div>
          ))}
        </div>
      </PortalDropdown>
    </>
  );
};

// --------------------------------------------------------------------------
// UI COMPONENTS
// --------------------------------------------------------------------------

/* Status and "hand this to another team" share one control.
   They were a dropdown and a column side by side, which put an overlay and a
   button in the same few pixels at the right edge of the grid. Both answer the
   same question — where does this task go next — so they now answer it in one
   place, and the grid gets a column back.

   `canMove` is separate from `readOnly` on purpose: a task belonging to
   somebody else cannot have its status set (that would clock its owner in),
   but it can still be passed to another team, which is what the Move column
   allowed and what would otherwise be lost here. */
const StatusSelect = ({
  currentStatus,
  onChange,
  isAnyTaskInProgress,
  readOnly,
  onMove,
  canMove = false,
  moveHint,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null);

  // Nothing to offer: no status to set and nowhere to send it.
  const isInert = readOnly && !canMove;

  const getColorParams = (status) => {
    switch (status) {
      case "Complete":
        return { bg: "#e8f5e9", text: "#2e7d32", dot: "#2e7d32" };
      case "Testing":
        return { bg: "#e0f7fa", text: "#00838f", dot: "#00838f" };
      case "In Progress":
        return { bg: "#e3f2fd", text: "#0d6efd", dot: "#0d6efd" };
      case "On Hold":
        return { bg: "#fff8e1", text: "#f57c00", dot: "#f57c00" };
      case "Not Possible":
        return { bg: "#ffebee", text: "#d32f2f", dot: "#d32f2f" };
      default:
        return { bg: "#f8f9fa", text: "#6c757d", dot: "#adb5bd" };
    }
  };
  const currentStyle = getColorParams(currentStatus);

  useEffect(() => {
    const handleClose = () => setIsOpen(false);
    if (isOpen) {
      window.addEventListener("click", handleClose);
      window.addEventListener("scroll", handleClose, true);
      document.addEventListener("close-dropdowns", handleClose);
    }
    return () => {
      window.removeEventListener("click", handleClose);
      window.removeEventListener("scroll", handleClose, true);
      document.removeEventListener("close-dropdowns", handleClose);
    };
  }, [isOpen]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (isInert) return;
    if (isOpen) return setIsOpen(false);
    document.dispatchEvent(new CustomEvent("close-dropdowns"));

    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      // Wide enough for "Move to another team", which is the longest line.
      const width = Math.max(rect.width, 210);

      // Status is the last column, so a left-anchored menu overflows the right
      // edge and gets clipped. Right-align it to the trigger when that happens,
      // and never let it slip past either viewport edge.
      let left = rect.left;
      if (left + width > viewportWidth - 8) {
        left = rect.right - width;
      }
      if (left < 8) left = 8;

      /* Placement only — the surface itself (background, radius, z-index,
         border) belongs to PortalDropdown, so the two cannot drift apart. */
      let newStyles = {
        left: `${left}px`,
        width: `${width}px`,
      };

      if (viewportHeight - rect.bottom < 220) {
        newStyles.bottom = `${viewportHeight - rect.top + 5}px`;
        newStyles.top = "auto";
      } else {
        newStyles.top = `${rect.bottom + 5}px`;
        newStyles.bottom = "auto";
      }
      setMenuStyle(newStyles);
      setIsOpen(true);
    }
  };

  if (isInert) {
    return (
      <div
        className="d-flex align-items-center gap-2"
        style={{
          backgroundColor: currentStyle.bg,
          color: currentStyle.text,
          padding: "6px 14px",
          borderRadius: "20px",
          fontSize: "0.75rem",
          fontWeight: "600",
          width: "fit-content",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: currentStyle.dot,
          }}
        ></div>
        <span>{displayStatus(currentStatus) || "Not Started"}</span>
      </div>
    );
  }

  return (
    <>
      <div ref={wrapperRef} className="position-relative d-inline-block">
        <button
          type="button"
          onClick={toggleMenu}
          className="d-flex align-items-center gap-2 shadow-sm border-0"
          style={{
            backgroundColor: currentStyle.bg,
            color: currentStyle.text,
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "0.75rem",
            fontWeight: "600",
            cursor: "pointer",
            minWidth: "125px",
            justifyContent: "space-between",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: currentStyle.dot,
              }}
            ></div>
            <span>{displayStatus(currentStatus) || "Not Started"}</span>
          </div>
          <i
            className="bi bi-chevron-down"
            style={{ fontSize: "0.6rem", opacity: 0.7 }}
          ></i>
        </button>
      </div>
      <PortalDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        style={menuStyle}
      >
        <div className="p-1 overflow-auto" style={{ maxHeight: "300px" }}>
          {(readOnly
            ? []
            : [
                "Not Started",
                "In Progress",
                "On Hold",
                "Not Possible",
                "Testing",
                "Complete",
              ]
          ).map((opt) => {
            const optStyle = getColorParams(opt);
            const isSelected = opt === currentStatus;
            let isDisabled = false;

            if (
              currentStatus === "Not Started" &&
              !["In Progress", "Not Started", "Not Possible"].includes(opt)
            )
              isDisabled = true;
            if (
              currentStatus === "On Hold" &&
              !["In Progress", "On Hold", "Not Possible"].includes(opt)
            )
              isDisabled = true;
            if (
              opt === "In Progress" &&
              isAnyTaskInProgress &&
              currentStatus !== "In Progress"
            )
              isDisabled = true;
            if (currentStatus !== "Not Started" && opt === "Not Started")
              isDisabled = true;
            if (opt === "Testing" && currentStatus !== "In Progress")
              isDisabled = true;

            return (
              <div
                key={opt}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled && !isSelected) {
                    onChange(opt);
                    setIsOpen(false);
                  }
                }}
                className="d-flex align-items-center px-3 py-2 rounded-2 my-1"
                style={{
                  cursor: isDisabled || isSelected ? "default" : "pointer",
                  fontSize: "0.8rem",
                  color: isDisabled ? "#adb5bd" : "#495057",
                  backgroundColor: isSelected ? "#f8f9fa" : "transparent",
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: optStyle.dot,
                    marginRight: "10px",
                  }}
                ></div>
                <span className="flex-grow-1">{opt}</span>
                {isDisabled && <i className="bi bi-lock-fill small"></i>}
              </div>
            );
          })}

          {canMove && (
            <>
              {/* Below the rule is a different kind of action: the ones above
                  change this task's state, this one gives it away. */}
              {!readOnly && (
                <div
                  style={{
                    borderTop: "1px solid #eef1f6",
                    margin: "6px 10px",
                  }}
                />
              )}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onMove?.();
                }}
                title={moveHint}
                className="d-flex align-items-center px-3 py-2 rounded-2 my-1"
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#eef2ff")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
                style={{
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#4f46e5",
                  transition: "background-color 0.15s ease",
                }}
              >
                <i
                  className="bi bi-arrow-left-right"
                  style={{ marginRight: "10px" }}
                  aria-hidden="true"
                />
                <span className="flex-grow-1">Move to another team</span>
              </div>
            </>
          )}
        </div>
      </PortalDropdown>
    </>
  );
};

const DescriptionCell = ({ value, onEdit, readOnly }) => {
  const truncateText = (str) =>
    str && str.length > 20 ? str.substring(0, 20) + "..." : str;
  if (readOnly)
    return (
      <span className="text-muted small" title={value}>
        {truncateText(value) || "-"}
      </span>
    );
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
      className="text-truncate text-muted d-flex align-items-center"
      title={value}
      style={{ cursor: "pointer", maxWidth: "200px", fontSize: "0.85rem" }}
    >
      <span className="me-2">
        {value ? truncateText(value) : "Add Description..."}
      </span>
      <i
        className="bi bi-pencil text-primary"
        style={{ fontSize: "0.7rem", opacity: 0.7 }}
      ></i>
    </div>
  );
};

// --------------------------------------------------------------------------
// MAIN COMPONENT
// --------------------------------------------------------------------------

const DevelopmentTask = () => {
  const dispatch = useDispatch();
  const [viewFilter, setViewFilter] = useState("Incomplete");
  /* { start, end } — the keys handleDateChange writes and every reader below
     uses. It was seeded as { startDate, endDate }, so the default existed but
     nothing could see it: the picker read .start and got undefined, and the
     first fetch went out with no dates at all. */
  const [dateRange, setDateRange] = useState(lastSevenDays);

  const [localTasks, setLocalTasks] = useState([]);

  // --- MODAL & ACTION STATES ---
  const [editDescTask, setEditDescTask] = useState(null);
  const [showDescModal, setShowDescModal] = useState(false);
  const [descText, setDescText] = useState("");
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdReasonText, setHoldReasonText] = useState("");
  const [pendingHoldData, setPendingHoldData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tasksData = useSelector(selectGetTaskDevelopmentList);
  const listStatus = useSelector(selectGetTaskDevelopmentListStatus);

  /* Hand-overs + status changes, merged server-side. This replaced the old
     history-tracker call outright: that endpoint returned status changes only
     and its rows are already included here, so keeping it would have meant a
     second round trip whose results nothing reads. */
  const timeline = useSelector(selectTaskTimeline);
  const timelineStatus = useSelector(selectTaskTimelineStatus);

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [taskToMove, setTaskToMove] = useState(null);
  /* The task that has just been completed and might need to go on to another
     team. Held separately from taskToMove so the prompt can be dismissed
     without opening the move dialog. */
  const [handoverAsk, setHandoverAsk] = useState(null);

  /* Opens on your own queue. This is the screen you work from — you come here
     to pick up the next thing and set its status, and a list led by other
     people's rows buries yours and makes the one-task-at-a-time rule read as
     if it were counting the whole company.

     "All Tasks" is still there, because being able to see where a task went
     after you handed it on is why the list was opened up in the first place.
     It is a thing you go and look at, not the thing you land on. */
  const [scope, setScope] = useState("mine");

  const currentUserId = useMemo(() => {
    try {
      return Number(JSON.parse(localStorage.getItem("user"))?.userId) || null;
    } catch {
      return null;
    }
  }, []);

  const isMyTask = useCallback(
    (row) => Number(row?.AssigneeCode) === currentUserId,
    [currentUserId],
  );

  /* One task at a time, per person. Only my own rows count — the whole
     company's In Progress tasks would block everybody the moment a single
     colleague started working.

     Both statuses count, because both mean "this is the task I am on":
     sp_Queries_TaskDevelopment_Update refuses a start while either is open,
     so leaving 'Testing In Progress' out here offered a start that the server
     then threw back. Holding or completing the task clears the status and
     with it this flag, which is what frees the next task up. */
  /* The one task the person is on, not merely whether there is one.
   *
   * isAnyTaskInProgress below is the same test reduced to a boolean — it gates
   * the status dropdown so a second task cannot be started. Deriving both from
   * one find keeps the header strip and that rule describing the same task:
   * two independent searches would eventually disagree, and the disagreement
   * would be a strip showing one task while the grid refuses to start another.
   *
   * Undefined when the current filter hides it — looking at Complete, or at
   * All Tasks having scrolled past it. The strip then shows nothing rather
   * than a stale row. */
  const runningTask = useMemo(
    () =>
      localTasks.find(
        (t) => ACTIVE_STATUSES.includes(t.Status) && isMyTask(t),
      ),
    [localTasks, isMyTask],
  );

  /* Counted over localTasks — everything this screen holds — so the chips
     describe the whole list rather than whatever the column filters have left
     showing. The grid is handed the filtered rows. */
  const {
    rows: targetRows,
    counts: targetCounts,
    mode: targetMode,
    toggle: toggleTarget,
  } = useTargetDateFilter(localTasks);

  const isAnyTaskInProgress = Boolean(runningTask);

  const windowScrollRef = useRef(0);
  const tableScrollRef = useRef(0);
  const lastRequestRef = useRef(null);

  /**
   * Moving a task to In Progress clocks the user in if they forgot to, so the
   * header Clock In / Clock Out button has to be refreshed afterwards —
   * otherwise it keeps its old label and the punch looks like it never
   * happened. The API reports what it did in `autoPunch`.
   */
  const announceAutoPunch = (res) => {
    const autoPunch = res?.payload?.data?.autoPunch;
    if (!autoPunch?.applied) return;

    dispatch(getStatusClock());
    toast.success("You have been clocked in.");
  };

  /* `force` re-asks with the parameters it just used. The de-dupe below exists
     for StrictMode's double-invoke and for re-renders, but a status change
     leaves every parameter identical while changing the answer — without the
     escape hatch the refresh after a save was silently dropped and the grid
     showed only what it had optimistically assumed. */
  const fetchTasks = (force = false) => {
    const apiParams = {
      isManuallyRaised: null,
      currentPage,
      pageSize,
      edit: viewFilter === "Complete" ? 1 : 0,
      scope,
      fromDate:
        dateRange.start && viewFilter !== "Incomplete"
          ? format(dateRange.start, "yyyy-MM-dd")
          : null,
      toDate:
        dateRange.end && viewFilter !== "Incomplete"
          ? format(dateRange.end, "yyyy-MM-dd")
          : null,
    };

    // Skip duplicate calls (StrictMode double-invoke + re-renders with same params).
    const requestKey = JSON.stringify(apiParams);
    if (!force && lastRequestRef.current === requestKey) return;
    lastRequestRef.current = requestKey;

    dispatch(getTaskDevelopmentApis(apiParams));
  };

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, pageSize, viewFilter, dateRange, scope]);

  /* Coming back to this tab re-asks for the list.
   *
   * Tabs stay mounted, so a screen left open an hour ago is showing an
   * hour-old answer — and this one goes stale faster than most, because the
   * whole team is moving these rows around while you are on another tab.
   *
   * force: true, because every parameter is identical to last time. The
   * de-dupe in fetchTasks exists for StrictMode and re-renders; the whole
   * point here is that the DATA has moved, not the request. */
  useTabRefresh(() => fetchTasks(true));

  useEffect(() => {
    if (selectedTask && showDetailModal) {
      dispatch(taskTimelineApis({ queryCode: selectedTask.QueryCode }));
    }
  }, [selectedTask, showDetailModal, dispatch]);

  useEffect(() => {
    const rawData = tasksData?.data;
    const list = Array.isArray(rawData) ? rawData : rawData?.data || [];

    console.log("API List", list);
    if (list) {
      setLocalTasks(
        list.map((task) => ({
          ...task,
          totalTimeMs: Number(task?.totalTimeMs) || 0,
          currentSessionStart: task?.currentSessionStart || null,
          Status: task?.Status || "Not Started",
        })),
      );
    }
  }, [tasksData]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (localTasks.some((t) => t.Status === "In Progress"))
        setLocalTasks((prev) => [...prev]);
    }, 60000);
    return () => clearInterval(interval);
  }, [localTasks]);

  const handleDateChange = (start, end) => {
    setDateRange({ start, end });
    if ((!start && !end) || (start && end)) setCurrentPage(1);
  };

  const handleExport = (data) => alert(`Exporting ${data.length} tasks...`);

  // --- SCROLL LOCK FUNCTIONS ---
  const handleRowClick = useCallback((row) => {
    windowScrollRef.current =
      window.scrollY || document.documentElement.scrollTop;

    const tableWrapper =
      document.querySelector(".table-responsive") ||
      document.querySelector(".rt-tbody") ||
      document.querySelector(".report-container table")?.parentElement;

    if (tableWrapper) {
      tableScrollRef.current = tableWrapper.scrollTop;
    }

    setSelectedTask(row);
    setShowDetailModal(true);
  }, []);

  const closeDetailModal = useCallback(() => {
    const savedWindowY = windowScrollRef.current;
    const savedTableY = tableScrollRef.current;

    setShowDetailModal(false);
    setSelectedTask(null);

    const enforceInstantScroll = () => {
      document.documentElement.style.scrollBehavior = "auto";
      document.body.style.scrollBehavior = "auto";
      window.scrollTo({ top: savedWindowY, left: 0, behavior: "auto" });

      const tableWrapper =
        document.querySelector(".table-responsive") ||
        document.querySelector(".rt-tbody") ||
        document.querySelector(".report-container table")?.parentElement;

      if (tableWrapper) {
        tableWrapper.style.scrollBehavior = "auto";
        tableWrapper.scrollTop = savedTableY;
      }

      setTimeout(() => {
        document.documentElement.style.scrollBehavior = "";
        document.body.style.scrollBehavior = "";
        if (tableWrapper) tableWrapper.style.scrollBehavior = "";
      }, 10);
    };

    enforceInstantScroll();
    requestAnimationFrame(enforceInstantScroll);
  }, []);

  const syncWithBackend = (task, type) => {
    setLoading(true);
    let payload = { queryCode: task.QueryCode };

    /* Set once the server has accepted the start, never before it. The reload
       used to be queued here on a timer: when the server refused a second
       concurrent task, the page reloaded anyway, taking the refusal toast
       with it, and the row simply snapped back to its old status with nothing
       said. Deferred to the success path below instead. */
    let reloadAfterStart = false;

    if (task?.Status === "In Progress") {
      localStorage.setItem("workStatus", "Active");
      payload.startTime = formatDateForPayload(task.StartTime);
      payload.status = "In Progress";
      payload.description = task.Description;
      payload.isUpdate = task.isUpdate;
      reloadAfterStart = true;
    } else if (task?.Status === "Complete" || task?.Status === "Testing") {
      payload.endTime = formatDateForPayload(task.EndTime);
      payload.status = task.Status;
      payload.startTime = formatDateForPayload(task.StartTime);
      payload.description = task.Description;
      payload.timeTaken = task.TotalTimeTaken_Seconds;
      payload.isUpdate = task.isUpdate;
    } else if (task?.Status === "On Hold" || task?.Status === "Not Possible") {
      payload.startTime = formatDateForPayload(task.StartTime);
      payload.status = task?.Status === "On Hold" ? "On Hold" : "Not Possible";
      payload.description = task.Description;
      payload.isUpdate = task.isUpdate;
    } else if (type === "DESCRIPTION") {
      payload.description = task.Description || "";
      payload.status = task?.Status;
      payload.isUpdate = task.isUpdate;
    } else {
      payload.startTime = null;
      payload.endTime = null;
      payload.status = task?.Status;
      payload.isUpdate = task.isUpdate;
    }

    const statusTrackerPayload = {
      queryCode: task.QueryCode,
      status: task?.Status,
      holdReason: payload.status == "On Hold" ? task?.holdReason : null,
    };
    console.log(payload, "payload 324212");

    /* Starting a task clocks the user in if they had forgotten to, so the
       header has to catch up afterwards. That used to be done by reloading the
       whole page — see the start branch below. */
    const afterUpdate = (res) => {
      setLoading(false);
      announceAutoPunch(res);

      if (!updateDevelopmentTasksApis.fulfilled.match(res)) {
        // Server said no. Put the row back the way it really is.
        fetchTasks(true);
        return;
      }

      if (reloadAfterStart) {
        /* Was `setTimeout(() => window.location.reload(), 1000)`.
         *
         * Starting a task clocks you in, and the header's Clock In / Clock Out
         * button and Active pill both had to be told. Reloading the page told
         * them — along with throwing away every open tab, the scroll position,
         * the filters, and about two seconds, every single time anybody pressed
         * In Progress. It is the "screen refreshed itself" this page kept being
         * reported for.
         *
         * Nothing about it was necessary. Both readouts come from
         * dashboard.clockStatus and dashboard.workStatus, and getStatusClock
         * sets both — it is what HeaderClockButton already calls on a timer,
         * on tab focus, and what announceAutoPunch calls here when the API
         * reports it punched somebody in. Asked for directly, the header
         * updates in place in one small request.
         *
         * Unconditional rather than left to announceAutoPunch: that only fires
         * when the API says it applied a punch, and a start still moves the
         * running-task state whether or not a punch came with it. */
        dispatch(getStatusClock());
        fetchTasks(true);
        return;
      }

      /* Completing your part is not the same as the task being finished.
         A Database developer marks their work Complete and the row leaves
         this list — at which point there was no way left to pass it to Web
         App, because Move only sits on tasks still in flight. Ask here,
         while the task is still in hand. */
      if (task?.Status === "Complete" && task?.TeamCode) {
        setHandoverAsk(task);
      }

      fetchTasks(true);
    };

    if (
      type === "DESCRIPTION" &&
      task.Status !== "Complete" &&
      task.Status !== "Testing" &&
      task.Status !== "On Hold"
    ) {
      dispatch(updateDevelopmentTasksApis(payload)).then(afterUpdate);
    } else {
      dispatch(createStatusTrackerApis(statusTrackerPayload)).then((value) => {
        if (value?.payload?.success) {
          if (
            task?.Status === "Complete" ||
            task?.Status === "Testing" ||
            task?.Status === "On Hold"
          ) {
            payload.timeTaken =
              value?.payload?.data?.[0]?.TotalTimeTaken_Seconds;
          }
          dispatch(updateDevelopmentTasksApis(payload)).then(afterUpdate);
        } else {
          setLoading(false);
        }
      });
    }
  };

  const handleStatusChange = (rowId, newStatus) => {
    const taskIndex = localTasks.findIndex((t) => t.QueryNo === rowId);
    if (taskIndex === -1) return;
    const task = localTasks[taskIndex];

    // Re-picking the status a task already has is a no-op, and putting it
    // through added a duplicate "Changed to: <status>" row to the task history
    // timeline. On Hold is the exception — re-holding is how the hold reason
    // gets changed. The API drops the repeat too; this only saves the round
    // trip and tells the user why nothing moved.
    if (newStatus === task.Status && newStatus !== "On Hold") {
      toast.info(`Task is already ${newStatus}.`);
      return;
    }

    console.log("Task:", task);
    console.log("IsUpdate:", task.IsUpdate);
    console.log("isUpdate:", task.isUpdate);
    console.log("newStatus:", newStatus);
    console.log(task);


    const currentIsUpdate = task.IsUpdate || task.isUpdate;
    // if (
    //   newStatus !== "Not Started" &&
    //   (!currentIsUpdate ||
    //     currentIsUpdate === "None" ||
    //     currentIsUpdate === "null")
    // ) {
    //   toast.warning(
    //     "Please select a valid 'Is Update' option before updating the status.",
    //   );
    //   return;
    // }

    /* Testing, Complete and Not Possible each want a note about THIS
     * transition — what was tested, what was done, why it cannot be done. All
     * three now ask every time, and all three ask on a blank field.
     *
     * Complete and Not Possible used to ask only when the Description column
     * happened to be empty. A task that has been round the teams once already
     * carries the note from last time, so the guard passed and it completed
     * with somebody else's sentence attached — and on the occasions it did
     * ask, the box opened pre-filled with that same stale text, which is an
     * invitation to press Save on a description of different work.
     *
     * A required field that is already full is not a required field. */
    if (
      newStatus === "Testing" ||
      newStatus === "Complete" ||
      newStatus === "Not Possible"
    ) {
      setPendingStatusUpdate({ rowId, newStatus });
      openDescriptionModal(task, { blank: true });
      return;
    }

    if (newStatus === "On Hold") {
      setPendingHoldData({ rowId, task });
      setHoldReasonText("");
      setShowHoldModal(true);
      return;
    }

    const now = new Date();
    let updates = { Status: newStatus };
    if (newStatus === "In Progress" && !task.StartTime)
      updates.StartTime = now.toISOString();
    if (newStatus === "Complete" || newStatus === "Testing")
      updates.EndTime = now.toISOString();

    const updatedTask = { ...task, ...updates };

    setLocalTasks((prev) => {
      const isTerminalState =
        newStatus === "Complete" || newStatus === "Testing";
      if (viewFilter === "Incomplete" && isTerminalState)
        return prev.filter((t) => t.QueryNo !== rowId);
      if (viewFilter === "Complete" && !isTerminalState)
        return prev.filter((t) => t.QueryNo !== rowId);
      return prev.map((t) => (t.QueryNo === rowId ? updatedTask : t));
    });

    syncWithBackend(updatedTask);
  };

  const handleIsUpdateChange = async (task, newValue) => {

    setLoading(true);
    setLocalTasks((prev) =>
      prev.map((t) =>
        t.QueryNo === task.QueryNo
          ? { ...t, IsUpdate: newValue, isUpdate: newValue }
          : t,
      ),
    );

    const payload = {
      queryCode: task.QueryCode || task.QueryNo,
      isUpdate: newValue,
      status: task.Status || "Not Started",
    };

    console.log("Payload", payload);


    try {
      const res = await dispatch(updateDevelopmentTasksApis(payload));

      console.log("Response:", res);

      if (res?.payload?.success) {
        toast.success("Is Update status changed!");
        fetchTasks(true);
      } else {
        // The thunk has already shown the server's reason.
        fetchTasks(true);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
      fetchTasks(true);
    } finally {
      setLoading(false);
    }
  };

  /* `blank` opens the box empty rather than on the existing note.
   *
   * Editing a description should start from what is there; being ASKED for one
   * as part of a status change should not, because the answer is about the
   * transition being made now, not about whatever the task said before. */
  const openDescriptionModal = (task, { blank = false } = {}) => {
    setEditDescTask(task);
    setDescText(blank ? "" : task.Description || "");
    setShowDescModal(true);
  };

  const handleSaveDescription = () => {
    if (!editDescTask) return;

    // --- ONLY CHECK IF EMPTY. NO isValidText RULES ---
    if (!descText || descText.trim() === "") {
      toast.error("Description cannot be empty. Please enter a description.");
      return;
    }

    const now = new Date();
    let updates = { Description: descText.trim() };

    if (
      pendingStatusUpdate &&
      pendingStatusUpdate.rowId === editDescTask.QueryNo
    ) {
      updates.Status = pendingStatusUpdate.newStatus;
      if (updates.Status === "Complete" || updates.Status === "Testing")
        updates.EndTime = now.toISOString();
    }

    const updatedTask = { ...editDescTask, ...updates };

    setLocalTasks((prev) => {
      const isTerminalState =
        updatedTask.Status === "Complete" || updatedTask.Status === "Testing";
      if (viewFilter === "Incomplete" && isTerminalState)
        return prev.filter((t) => t.QueryNo !== updatedTask.QueryNo);
      return prev.map((t) =>
        t.QueryNo === updatedTask.QueryNo ? updatedTask : t,
      );
    });

    if (pendingStatusUpdate) syncWithBackend(updatedTask);
    else syncWithBackend(updatedTask, "DESCRIPTION");

    setShowDescModal(false);
    setPendingStatusUpdate(null);
  };

  /* Cancel on the Task Completed dialog: put it back to In Progress.
   *
   * The completion is already written by the time that dialog opens — the
   * status write happens the moment Complete is picked, and the dialog is the
   * follow-up question about handover. So this is a real status change back,
   * not a dismissal: same path as any other, so it lands in the task history
   * as the reversal it is rather than quietly rewriting the record.
   *
   * Going back to In Progress clocks the person in again, which is correct —
   * they are working on it again — and is why this goes through
   * syncWithBackend rather than a bare API call.
   *
   * The server can still refuse, if they have started something else in the
   * meantime and the one-task-at-a-time rule bites. afterUpdate already
   * handles that: the refusal is shown and the row is re-read from the
   * server. */
  const undoCompletion = () => {
    const task = handoverAsk;
    setHandoverAsk(null);
    if (!task) return;

    const reverted = { ...task, Status: "In Progress", EndTime: null };

    setLocalTasks((prev) => {
      const known = prev.some((t) => t.QueryNo === reverted.QueryNo);
      /* The Incomplete view drops a task the moment it completes, so on that
         filter the row is gone by now and has to be put back rather than
         mapped over. */
      return known
        ? prev.map((t) => (t.QueryNo === reverted.QueryNo ? reverted : t))
        : [reverted, ...prev];
    });

    syncWithBackend(reverted);
  };

  const handleHoldSubmit = () => {
    // --- ONLY CHECK IF EMPTY. NO isValidText RULES ---
    if (!holdReasonText || holdReasonText.trim() === "") {
      toast.error("Hold reason cannot be empty. Please enter a reason.");
      return;
    }

    const { rowId, task } = pendingHoldData;
    const updatedTask = {
      ...task,
      Status: "On Hold",
      holdReason: holdReasonText.trim(),
    };

    setLocalTasks((prev) =>
      prev.map((t) => (t.QueryNo === rowId ? updatedTask : t)),
    );

    syncWithBackend(updatedTask);
    setShowHoldModal(false);
    setPendingHoldData(null);
  };

  const columns = useMemo(() => {
    const commonCols = [
      {
        label: "Client Name",
        /* Was key: "UserName" — a column labelled Client Name, showing the
           client, and filtering on a field that is neither. The header box
           read row.UserName, which these rows do not carry, so typing "SKT"
           into it matched nothing and the filter looked dead.

           searchValue and sortValue spell out the SAME fallback the cell
           renders. Without them the filter still misses every row that falls
           back to CompanyName, which is the half of the problem that would
           have survived renaming the key. */
        key: "ClientName",
        searchValue: (r) => r.ClientName || r.CompanyName || "",
        sortValue: (r) => r.ClientName || r.CompanyName || "",
        width: "130px",
        cellClass: "text-cell-wrap",
        render: (r) => (
          <div
            style={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              overflow: "hidden",
            }}
          >
            {r.ClientName || r.CompanyName || "-"}
          </div>
        ),
      },
      {
        label: "Form Name",
        key: "FormName",
        /* The cell falls back to MainMenuName, so the filter has to as well —
           every row showing "mnuCotton" from that fallback was unfindable by
           typing "mnuCotton". */
        searchValue: (r) => r.FormName || r.MainMenuName || "",
        sortValue: (r) => r.FormName || r.MainMenuName || "",
        width: "180px",
        cellClass: "text-cell-wrap",
        render: (r) => (
          <div
            style={{
              wordBreak: "break-all",
              whiteSpace: "normal",
              overflow: "hidden",
            }}
          >
            {r.FormName || r.MainMenuName || "-"}
          </div>
        ),
      },
      {
        label: "Task Name",
        key: "TaskName",
        /* Same fallback as the cell: a task with no TaskName shows its
           NatureofQuery, and searching for the words on screen has to find
           it. */
        searchValue: (r) => r.TaskName || r.NatureofQuery || "",
        sortValue: (r) => r.TaskName || r.NatureofQuery || "",
        width: "250px",
        cellClass: "text-cell-wrap",
        render: (r) => (
          <div
            onClick={() => handleRowClick(r)}
            className="text-primary fw-medium cursor-pointer"
            style={{
              cursor: "pointer",
              wordBreak: "break-word",
              whiteSpace: "normal",
              overflow: "hidden",
            }}
          >
            {r.TaskName || r.NatureofQuery}
          </div>
        ),
      },
      {
        /* Straight after the name, which is where Task Assignment and Tech
           Team Tasks put it — the number and the name are how a task gets
           referred to out loud, and they should be read together.

           searchValue without the "#": the number is what somebody types when
           they are looking for 8617, and the hash is decoration on the way
           out, not part of what is being matched. */
        label: "Query No",
        key: "QueryNo",
        width: "100px",
        cellClass: "nowrap-cell",
        searchValue: (r) => String(r.QueryNo ?? ""),
        sortValue: (r) => Number(r.QueryNo) || 0,
        render: (r) => (
          <span className="fw-bold text-secondary">#{r.QueryNo}</span>
        ),
      },
      {
        label: "Query Type",
        key: "QueryType",
        width: "150px",
        filterType: "select",
        options: QUERY_TYPE_FILTER_OPTIONS,
        searchValue: (r) => (r.QueryType || "").trim(),
        render: (r) => {
          let badgeClass = "bg-secondary";
          const type = r.QueryType ? r.QueryType.trim() : "";
          if (type === "Bug" || type === "BUG") badgeClass = "bg-danger";
          else if (type === "New Task") badgeClass = "bg-primary";
          else if (type === "Rework") badgeClass = "bg-warning text-dark";
          return (
            <span
              className={`badge ${badgeClass} rounded-pill`}
              style={{ padding: "6px 12px", letterSpacing: "0.5px" }}
            >
              {type || "-"}
            </span>
          );
        },
      },
      {
        label: "Start Time",
        key: "StartTime",
        type: "datetime",
        width: "140px",
        render: (r) => (
          <span className="text-muted small">
            {formatDateTimeDisplay(r.StartTime) || "-"}
          </span>
        ),
      },
      {
        label: "Is Update",
        key: "IsUpdate",
        width: "140px",
        type: "select",
        filterType: "select",
        options: ["None", "Backend work", "Update", "Not update"],
        searchValue: (r) => r.IsUpdate || r.isUpdate || "None",
        render: (r) => (
          <IsUpdateSelect
            initialValue={r.IsUpdate || r.isUpdate}
            onChange={(val) => handleIsUpdateChange(r, val)}
            readOnly={viewFilter === "Complete" || !isMyTask(r)}
          />
        ),
      },
      /* Whose task this is — but only on All Tasks.

         There it is essential: without it there is no way to tell your own
         rows from a colleague's, and the controls that lock on their rows
         just look broken.

         On My Tasks it cannot carry information. That list is filtered to
         AssigneeCode === currentUserId, so every cell reads your own name
         with a "You" badge next to it — a column of one repeated value, and
         a filter dropdown with one option in it, taking 130px from the
         columns that do vary. */
      ...(scope === "all"
        ? [
            {
              label: "Assignee",
              key: "AssigneeName",
              width: "130px",
              type: "select",
              filterType: "select",
              searchValue: (r) => r.AssigneeName || "Unassigned",
              render: (r) =>
                r.AssigneeName ? (
                  <span
                    className={isMyTask(r) ? "fw-bold text-dark" : "text-muted"}
                    style={{ fontSize: "0.82rem" }}
                  >
                    {r.AssigneeName}
                    {isMyTask(r) && (
                      <span
                        className="badge bg-primary ms-1"
                        style={{ fontSize: "0.6rem" }}
                      >
                        You
                      </span>
                    )}
                  </span>
                ) : (
                  <span
                    className="text-muted fst-italic"
                    style={{ fontSize: "0.78rem" }}
                  >
                    Unassigned
                  </span>
                ),
            },
          ]
        : []),
      {
        label: "Team",
        key: "TeamName",
        width: "110px",
        type: "select",
        filterType: "select",
        searchValue: (r) => r.TeamName || "",
        render: (r) => <TeamBadge name={r.TeamName} short={r.TeamShortName} />,
      },
    ];

    /* When the work is wanted by — set on the Assignment screen, which will
       not hand a task out without it. Read-only here: a developer is told the
       date, they do not set it, and moving it is a conversation with whoever
       assigned the task rather than a cell edit.

       Overdue is worth colouring on this screen and nowhere else. This is the
       list somebody works from, so "you are past the date on this one" is a
       thing to act on rather than a statistic. A finished task is left plain —
       it was late or it was not, and the row is history either way. */
    const targetDateCol = {
      label: "Target Date",
      /* A key with no field behind it: these rows arrive from the API
         unprocessed, so every read of the value goes through an accessor. The
         date filter is the one that needs saying out loud — without
         filterDateValue it reads row["TargetDateValue"], finds nothing, and
         filters every row away the moment somebody picks a range. */
      key: "TargetDateValue",
      width: "120px",
      type: "date",
      searchValue: (r) => formatDateOnly(r.TargetDate, ""),
      sortValue: (r) => toDateOnly(r.TargetDate),
      filterDateValue: (r) => toDateOnly(r.TargetDate),
      render: (r) => <TargetDateCell value={r.TargetDate} status={r.Status} />,
    };

    const descriptionCol = {
      label: "Description",
      key: "Description",
      width: "200px",
      render: (r) => (
        <DescriptionCell
          value={r.Description}
          onEdit={() => openDescriptionModal(r)}
          readOnly={viewFilter === "Complete" || !isMyTask(r)}
        />
      ),
    };
    const statusCol = {
      label: "Status",
      key: "Status",
      width: "170px",
      filterType: "select",
      options: STATUS_FILTER_OPTIONS,
      /* displayStatus is what the cell shows, so the filter has to match on
         it too — filtering on the raw value would miss the rows whose label
         differs from what is stored. */
      searchValue: (r) => displayStatus(r.Status) || "Not Started",
      render: (r) => (
        <StatusSelect
          currentStatus={r.Status}
          /* Read-only on somebody else's task, and not out of politeness:
             setting a task to In Progress clocks its OWNER in. Leaving this
             editable would let one person start another person's shift by
             misclicking a row in a list that now shows everyone's work. */
          readOnly={viewFilter === "Complete" || !isMyTask(r)}
          onChange={(val) => handleStatusChange(r.QueryNo, val)}
          isAnyTaskInProgress={isAnyTaskInProgress}
          /* Only on work in flight, and only once it has a team to move from.
             A finished task has already been handed on; offering to move it
             again just invites mistakes. */
          canMove={viewFilter === "Incomplete" && Boolean(r.TeamCode)}
          moveHint="Hand this task to another team"
          onMove={() => {
            setTaskToMove(r);
            setShowMoveModal(true);
          }}
        />
      ),
    };

    if (viewFilter === "Incomplete") {
      return [
        ...commonCols,
        {
          label: "Est. Time",
          key: "EstimateTime",
          width: "100px",
          render: (r) => r.EstimateTime || "-",
        },
        // Beside Est. Time, because they answer the same question from two
        // ends: how long it should take, and when it is wanted by.
        targetDateCol,
        /* Status before Description, on both views.
           Description is the widest column and the only one that wraps to
           several lines, so anything after it sat past a ragged right edge
           and, on a narrow window, past the horizontal scroll as well —
           which put the control people reach for most out of sight. */
        // Move lives inside statusCol's dropdown — see StatusSelect.
        statusCol,
        descriptionCol,
      ];
    } else {
      return [
        ...commonCols,
        {
          label: "End Time",
          key: "EndTime",
          type: "datetime",
          width: "140px",
          render: (r) => (
            <span className="text-muted small">
              {formatDateTimeDisplay(r.EndTime) || "-"}
            </span>
          ),
        },
        {
          label: "Total Time",
          key: "TotalTimeTaken",
          width: "100px",
          render: (r) => r.TotalTimeTaken || "-",
        },
        targetDateCol,
        statusCol,
        descriptionCol,
      ];
    }
  }, [viewFilter, scope, isAnyTaskInProgress, localTasks, handleRowClick, isMyTask]);


  return (
    <div className="report-container">
      {/* <div className="page-report-header">
        <h2 className="report-title">Development Tasks</h2>
        <div className="header-right-controls">
          {viewFilter == "Complete" ? (
            <DateRangeFilter
              startDate={dateRange.start}
              endDate={dateRange.end}
              onChange={handleDateChange}
            />
          ) : (
            <></>
          )}
          <div className="header-btn-group">
            <button
              className={`header-btn ${viewFilter === "Incomplete" ? "btn-incomplete" : "btn-complete"}`}
              onClick={() => {
                const newVal =
                  viewFilter === "Incomplete" ? "Complete" : "Incomplete";
                setViewFilter(newVal);
                setCurrentPage(1);
              }}
            >
              <i
                className={`bi ${viewFilter === "Incomplete" ? "bi-hourglass-split" : "bi-check-circle-fill"}`}
              ></i>{" "}
              {viewFilter}
            </button>
          </div>
        </div>
      </div> */}

      <PageHeader
        className="development-header"
        icon={<i className="bi bi-code-slash"></i>}
        title="Development Tasks"
        /* The live readout goes in the subtitle slot: the wide empty band
           beside the title, which is the first place the eye lands and was
           carrying nothing. */
        subtitle={
          runningTask || targetCounts.due || targetCounts.overdue ? (
            <div className="d-flex align-items-center flex-wrap gap-2">
              {runningTask && <RunningTaskStrip task={runningTask} />}
              <TargetDateChips
                counts={targetCounts}
                mode={targetMode}
                onToggle={toggleTarget}
              />
            </div>
          ) : undefined
        }
        actions={
          <div className="development-header-actions d-flex align-items-center gap-2">
            {viewFilter === "Complete" && (
              <DateRangeFilter
                startDate={dateRange.start}
                endDate={dateRange.end}
                onChange={handleDateChange}
              />
            )}

            {/* Your own queue is what the screen opens on. This widens it to
                everybody's — useful for following a task you handed on, and
                the only place unclaimed team-pool work shows up. */}
            <div
              className="btn-group"
              role="group"
              aria-label="Whose tasks to show"
            >
              {[
                { key: "mine", label: "My Tasks", icon: "bi-person-fill" },
                { key: "all", label: "All Tasks", icon: "bi-people-fill" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setScope(opt.key);
                    setCurrentPage(1);
                  }}
                  className="btn btn-sm d-inline-flex align-items-center gap-1"
                  style={{
                    backgroundColor: scope === opt.key ? "#eef2ff" : "#fff",
                    color: scope === opt.key ? "#4f46e5" : "#6c757d",
                    border: "1px solid #e2e8f0",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    padding: "6px 12px",
                  }}
                >
                  <i className={`bi ${opt.icon}`} aria-hidden="true" />
                  {opt.label}
                </button>
              ))}
            </div>

            <button
              className={`header-btn ${viewFilter === "Incomplete"
                ? "btn-incomplete"
                : "btn-complete"
                }`}
              onClick={() => {
                setViewFilter(
                  viewFilter === "Incomplete"
                    ? "Complete"
                    : "Incomplete"
                );
                setCurrentPage(1);
              }}
            >
              <i
                className={`bi ${viewFilter === "Incomplete"
                  ? "bi-hourglass-split"
                  : "bi-check-circle-fill"
                  }`}
              ></i>
              {viewFilter}
            </button>
          </div>
        }
      />
      <div
        className="development-table-wrapper"
        style={{ position: "relative", minHeight: "200px" }}
      >
        {/* The skeleton is for the FIRST load — when there is genuinely
            nothing to show yet.

            It used to cover the grid on every save as well, and <Loader />
            draws a whole PAGE skeleton (see components/Loader.jsx), so
            changing one row's status blanked the entire table for the length
            of a round trip. That reads as the screen reloading itself, which
            is exactly what it was reported as.

            It was also undoing the work above it: every one of these handlers
            updates the row in localTasks BEFORE it calls the server, so the
            new value is already on screen. Covering it with a skeleton hid the
            answer the user had just been given, then showed it again.

            A save now says so by changing the row, and by its toast. If it
            fails, fetchTasks(true) puts the row back the way the server says
            it really is. */}
        {(listStatus === "loading" || loading) && localTasks.length === 0 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              zIndex: 999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* inline: the wrapper around this is positioned precisely so
                the skeleton covers the grid and nothing else — which a fixed,
                full-viewport .container-loader ignored entirely. */}
            <Loader isLoading inline variant="table" rows={8} cols={6} />
          </div>
        )}
        <ReportTable
          data={targetRows}
          columns={columns}
          onExport={handleExport}
          stickyColumns={[]}
          /* Matched on QueryNo, which is what every other row lookup on this
             page uses, so the highlighted row is the same row the rest of the
             screen means. */
          /* The running row keeps priority: "this is what you are doing at
             this moment" outranks "this one is late", and a task can be both. */
          rowClassName={(r) => {
            if (runningTask && r.QueryNo === runningTask.QueryNo) {
              return "sk-row-running";
            }
            const state = targetDateState(r.TargetDate, r.Status);
            return state === "overdue"
              ? "is-target-over"
              : state === "due"
                ? "is-target-due"
                : "";
          }}
        />
      </div>

      <Modal
        show={showDescModal}
        onClose={() => {
          setShowDescModal(false);
          setPendingStatusUpdate(null);
        }}
        /* The title says which of the two jobs this box is doing. Opened from
           the Description cell it edits what is there; opened by a status
           change it is asking for a note about that change, on an empty
           field — and calling that "Edit Description" made a required answer
           look like an optional tidy-up. */
        title={
          {
            Testing: "Reason for Testing",
            Complete: "What was completed?",
            "Not Possible": "Why is this not possible?",
          }[pendingStatusUpdate?.newStatus] || "Edit Description"
        }
        maxWidth="500px"
      >
        <div className="p-3">
          {pendingStatusUpdate && (
            <label className="form-label fw-bold small text-muted text-uppercase mb-2">
              {{
                Testing: "Please enter the testing reason",
                Complete: "Describe the work you finished",
                "Not Possible": "Explain why this cannot be done",
              }[pendingStatusUpdate.newStatus] || "Description"}{" "}
              <span className="text-danger">*</span>
            </label>
          )}
          <textarea
            className="form-control mb-3"
            rows="5"
            value={descText}
            onChange={(e) => setDescText(e.target.value)}
            placeholder={
              pendingStatusUpdate
                ? "This is recorded against the task, and it is what the next team reads."
                : ""
            }
            autoFocus
          />
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-sm btn-light border"
              onClick={() => {
                setShowDescModal(false);
                setPendingStatusUpdate(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSaveDescription}
            >
              {{
                Testing: "Submit & Start Testing",
                Complete: "Save & Complete",
                "Not Possible": "Save & Mark Not Possible",
              }[pendingStatusUpdate?.newStatus] || "Save"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        show={showHoldModal}
        onClose={() => {
          setShowHoldModal(false);
          setPendingHoldData(null);
        }}
        title="Hold Reason"
        maxWidth="500px"
      >
        <div className="p-3">
          <label className="form-label fw-bold small text-muted text-uppercase">
            Reason for Hold *
          </label>
          <textarea
            className="form-control mb-3"
            rows="4"
            value={holdReasonText}
            onChange={(e) => setHoldReasonText(e.target.value)}
          />
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-sm btn-light border"
              onClick={() => {
                setShowHoldModal(false);
                setPendingHoldData(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-warning text-dark fw-bold"
              onClick={handleHoldSubmit}
            >
              Submit Hold
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        show={showDetailModal}
        onClose={closeDetailModal}
        title="Task Details"
        maxWidth="900px"
      >
        {selectedTask && (
          <TaskDetailContent
            task={selectedTask}
            timeline={timeline}
            timelineStatus={timelineStatus}
          />
        )}
      </Modal>

      <MoveTaskModal
        show={showMoveModal}
        task={taskToMove}
        onClose={() => {
          setShowMoveModal(false);
          setTaskToMove(null);
        }}
        onMoved={() => {
          // The task has left this developer's list, so the grid must be
          // re-read rather than patched — it is no longer theirs to show.
          fetchTasks(true);
        }}
      />

      {/* Asked the moment a task is completed, because that is the only
          moment the person who did the work knows whether anyone else still
          needs it. Answering No is a real answer — most tasks are finished
          when they are finished — so it closes and nothing more happens. */}
      <Modal
        show={Boolean(handoverAsk)}
        onClose={() => setHandoverAsk(null)}
        title="Task Completed"
        maxWidth="520px"
        /* Still no ✕, now that Cancel does the job people were pressing it
         * for. The task is ALREADY complete when this opens — the status write
         * happens when Complete is chosen — so a close cross that merely
         * dismissed the dialog left the completion standing while looking like
         * an escape hatch. Cancel below actually reverses it; a second control
         * that looks the same and does not would put the confusion straight
         * back. */
        hideClose
      >
        {/* One left edge for everything.

            The task name used to sit 60px in — pushed there by a floating
            icon — while the question below started at the padding edge, so the
            dialog read as two blocks that had been laid out separately. The
            icon now lives INSIDE a panel, which makes that indent deliberate
            rather than accidental, and every line of body text starts in the
            same place.

            The shape deliberately matches Move Task to Another Team, because
            that is the very next dialog you see if you answer Yes. */}
        <form
          className="p-4 pt-3"
          style={{ "--sk-field-accent": "#4f46e5" }}
          onSubmit={(e) => e.preventDefault()}
        >
          {/* WHAT JUST HAPPENED */}
          <div
            className="d-flex align-items-start gap-3 mb-3"
            style={{
              background: "#f6fbf7",
              border: "1px solid #d9ecdd",
              borderRadius: "12px",
              padding: "12px 14px",
            }}
          >
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
                fontSize: "1.05rem",
              }}
            >
              <i className="bi bi-check2-circle" />
            </div>
            <div className="min-w-0">
              <div
                className="text-uppercase fw-bold"
                style={{
                  fontSize: "0.65rem",
                  letterSpacing: "0.05em",
                  color: "#2e7d32",
                }}
              >
                Completed
                {handoverAsk?.QueryNo ? ` · #${handoverAsk.QueryNo}` : ""}
              </div>
              <div
                className="fw-semibold"
                style={{
                  wordBreak: "break-word",
                  color: "#1f2937",
                  lineHeight: 1.35,
                }}
              >
                {handoverAsk?.TaskName || handoverAsk?.NatureofQuery}
              </div>
              <div
                className="d-flex align-items-center gap-2 mt-1 flex-wrap"
                style={{ fontSize: "0.75rem", color: "#64748b" }}
              >
                <span>by</span>
                <TeamBadge
                  name={handoverAsk?.TeamName}
                  short={handoverAsk?.TeamShortName}
                />
              </div>
            </div>
          </div>

          {/* THE QUESTION */}
          <p
            className="fw-semibold mb-1"
            style={{ color: "#1f2937", fontSize: "0.95rem" }}
          >
            Does another team still need to work on this?
          </p>
          <p
            className="text-muted mb-0"
            style={{ fontSize: "0.8rem", lineHeight: 1.5 }}
          >
            Passing it on records your team's part as complete in the task
            history, and the receiving team picks it up as new work. If you
            completed it by mistake, Cancel puts it back to In&nbsp;Progress.
          </p>

          {/* FOOTER — Cancel on the left, the two answers on the right. */}
          <div
            className="d-flex align-items-center justify-content-between gap-3 flex-wrap mt-4 pt-3"
            style={{ borderTop: "1px solid #eef1f6" }}
          >
            {/* Cancel sits away from the two answers, on the far side of the
                row. It is not a third answer to the question — it undoes the
                thing the question is about — and putting it beside the other
                two would make "finished / moved on / cancelled" read as three
                equal outcomes. */}
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center gap-1"
              style={{ fontSize: "0.78rem", color: "#64748b" }}
              onClick={undoCompletion}
            >
              <i className="bi bi-arrow-counterclockwise" />
              Cancel — back to In Progress
            </button>

            {/* Finished is the primary action, and it sits on the right.
             *
             * It was the other way round: handing on was the blue button in
             * the rightmost position, which is where the eye goes and where a
             * dialog puts the thing it expects you to do. Most tasks are
             * finished when they are finished — passing one on is the
             * exception — so the emphasis was recommending the rarer answer.
             *
             * Handing on keeps its icon, which is enough to find it by
             * without dressing it as the default. */}
            <div className="d-flex gap-2 ms-auto">
              <button
                type="button"
                className="btn btn-light border d-inline-flex align-items-center gap-2"
                style={{ whiteSpace: "nowrap" }}
                onClick={() => {
                  setTaskToMove(handoverAsk);
                  setHandoverAsk(null);
                  setShowMoveModal(true);
                }}
              >
                <i className="bi bi-arrow-left-right" />
                Yes, move it on
              </button>
              <button
                type="button"
                className="btn"
                style={{
                  backgroundColor: "#4f46e5",
                  borderColor: "#4f46e5",
                  color: "#fff",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
                onClick={() => setHandoverAsk(null)}
              >
                No, it's finished
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DevelopmentTask;